import API_BASE from '../config';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import ReplyEditor from '../components/ReplyEditor';

const BRAND = '#8B1A1A';

const STRATEGIES = [
  { value: 'least_used', label: 'Least used (most headroom)' },
  { value: 'round_robin', label: 'Round robin' },
  { value: 'weighted', label: 'Weighted by priority' },
];

function ProviderPill({ provider }) {
  return (
    <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#555', backgroundColor: '#F4F4F4', padding: '0.1rem 0.4rem', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {provider || 'unknown'}
    </span>
  );
}

function StatusDot({ status }) {
  const c = status === 'active' ? '#38A169' : status === 'error' ? '#E53E3E' : '#A0AEC0';
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: c, marginRight: 6 }} />;
}

export default function Personas() {
  const { id: clientId } = useParams();
  const { getToken } = useAuth();

  const [personas, setPersonas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [clientMailboxes, setClientMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }, [getToken]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const [pRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/clients/${clientId}/personas`, { headers }),
        fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
      ]);
      const pData = await pRes.json();
      const mData = await mRes.json();
      setPersonas(Array.isArray(pData) ? pData : []);
      setClientMailboxes(Array.isArray(mData) ? mData : []);
    } catch (e) {
      toast.error('Could not load personas');
    } finally {
      setLoading(false);
    }
  }, [clientId, authHeaders]);

  const loadDetail = useCallback(async (pid) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${pid}`, { headers });
      const data = await res.json();
      setDetail(data);
    } catch (e) {
      toast.error('Could not load persona');
    }
  }, [authHeaders]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId, loadDetail]);

  async function createPersona() {
    if (!newName.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/clients/${clientId}/personas`, {
        method: 'POST', headers, body: JSON.stringify({ display_name: newName.trim() }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setNewName('');
      await loadList();
      setSelectedId(created.id);
      toast.success('Persona created');
    } catch (e) {
      toast.error('Create failed');
    } finally {
      setCreating(false);
    }
  }

  async function saveDetail(patch) {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${detail.id}`, {
        method: 'PUT', headers, body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setDetail(d => ({ ...d, ...updated }));
      await loadList();
      toast.success('Saved');
    } catch (e) {
      toast.error('Save failed');
    }
  }

  async function deletePersona() {
    if (!detail) return;
    if (!window.confirm(`Delete persona "${detail.display_name}"? In-flight sends keep running; it stops being selectable for new sends.`)) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/personas/${detail.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error();
      setSelectedId(null);
      await loadList();
      toast.success('Persona removed');
    } catch (e) {
      toast.error('Delete failed');
    }
  }

  async function toggleMailbox(mailboxId, isBacking) {
    try {
      const headers = await authHeaders();
      const url = `${API_BASE}/personas/${detail.id}/mailboxes/${mailboxId}`;
      const res = await fetch(url, { method: isBacking ? 'DELETE' : 'POST', headers, body: isBacking ? undefined : JSON.stringify({ priority: 0 }) });
      if (!res.ok) throw new Error();
      await loadDetail(detail.id);
      await loadList();
    } catch (e) {
      toast.error('Could not update backing mailbox');
    }
  }

  const backingIds = new Set((detail?.mailboxes || []).filter(m => m.is_active).map(m => m.id));

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: 'inherit' }}>
      {/* List */}
      <div style={{ width: 320, borderRight: '1px solid #EEE', padding: '1rem', overflowY: 'auto' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Personas</h2>
        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') createPersona(); }}
            placeholder="New persona name"
            style={{ flex: 1, padding: '0.4rem 0.5rem', border: '1px solid #DDD', borderRadius: 6, fontSize: '0.85rem' }}
          />
          <button onClick={createPersona} disabled={creating}
            style={{ backgroundColor: BRAND, color: '#FFF', border: 'none', borderRadius: 6, padding: '0.4rem 0.7rem', fontSize: '0.85rem', cursor: 'pointer' }}>
            Add
          </button>
        </div>
        {loading && <p style={{ color: '#888', fontSize: '0.85rem' }}>Loading…</p>}
        {!loading && personas.length === 0 && <p style={{ color: '#888', fontSize: '0.85rem' }}>No personas yet.</p>}
        {personas.map(p => (
          <div key={p.id} onClick={() => setSelectedId(p.id)}
            style={{ padding: '0.6rem 0.7rem', borderRadius: 8, cursor: 'pointer', marginBottom: '0.4rem',
                     backgroundColor: p.id === selectedId ? '#FBEAEA' : 'transparent',
                     border: p.id === selectedId ? `1px solid ${BRAND}` : '1px solid #EEE' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.display_name}</div>
            <div style={{ fontSize: '0.72rem', color: '#777', marginTop: 2 }}>
              {p.mailbox_count} mailbox{p.mailbox_count === '1' ? '' : 'es'} · {p.campaign_count} campaign{p.campaign_count === '1' ? '' : 's'}
            </div>
          </div>
        ))}
      </div>

      {/* Detail */}
      <div style={{ flex: 1, padding: '1.5rem', overflowY: 'auto' }}>
        {!detail && <p style={{ color: '#888' }}>Select a persona, or create one.</p>}
        {detail && (
          <div style={{ maxWidth: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{detail.display_name}</h2>
              <button onClick={deletePersona}
                style={{ background: 'none', border: '1px solid #E53E3E', color: '#E53E3E', borderRadius: 6, padding: '0.35rem 0.7rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                Delete
              </button>
            </div>

            {/* Branding fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '1.5rem' }}>
              <Field label="Display name" value={detail.display_name} onSave={v => saveDetail({ display_name: v })} />
              <Field label="Job title" value={detail.job_title} onSave={v => saveDetail({ job_title: v })} />
              <Field label="Reply-to email" value={detail.reply_to_email} onSave={v => saveDetail({ reply_to_email: v })} />
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rotation strategy</label>
                <select value={detail.rotation_strategy || 'least_used'} onChange={e => saveDetail({ rotation_strategy: e.target.value })}
                  style={{ width: '100%', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #DDD', borderRadius: 6, fontSize: '0.85rem' }}>
                  {STRATEGIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Signature */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Signature (HTML)</label>
              <SignatureEditor initial={detail.signature_html} onSave={html => saveDetail({ signature_html: html })} />
            </div>

            {/* Backing mailboxes */}
            <div>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 0.5rem' }}>Backing mailboxes</h3>
              <p style={{ fontSize: '0.78rem', color: '#777', margin: '0 0 0.75rem' }}>
                The engine rotates across these when sending as this persona. Recipients see one identity.
              </p>
              {clientMailboxes.length === 0 && <p style={{ color: '#888', fontSize: '0.85rem' }}>No mailboxes available to this client.</p>}
              {clientMailboxes.map(m => {
                const isBacking = backingIds.has(m.id);
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.6rem', border: '1px solid #EEE', borderRadius: 8, marginBottom: '0.4rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <StatusDot status={m.status} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{m.email_address}</span>
                      <ProviderPill provider={m.provider} />
                      <span style={{ fontSize: '0.72rem', color: '#999' }}>{m.sent_today}/{m.daily_cap}</span>
                    </div>
                    <button onClick={() => toggleMailbox(m.id, isBacking)}
                      style={{ background: isBacking ? '#FBEAEA' : BRAND, color: isBacking ? BRAND : '#FFF',
                               border: isBacking ? `1px solid ${BRAND}` : 'none', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer' }}>
                      {isBacking ? 'Remove' : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onSave }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <div>
      <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <input value={v} onChange={e => setV(e.target.value)} onBlur={() => { if (v !== (value || '')) onSave(v); }}
        style={{ width: '100%', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #DDD', borderRadius: 6, fontSize: '0.85rem' }} />
    </div>
  );
}

function SignatureEditor({ initial, onSave }) {
  const [v, setV] = useState(initial || '');
  const [dirty, setDirty] = useState(false);
  // Reset when switching personas. Keep a key so the editor remounts with fresh content.
  useEffect(() => { setV(initial || ''); setDirty(false); }, [initial]);
  return (
    <div style={{ marginTop: 4 }}>
      <ReplyEditor
        value={v}
        onChange={html => { setV(html); setDirty(true); }}
        placeholder="Jimmy Braimah, Founder at Webcite. Add your contact details, links and disclaimer."
      />
      <button onClick={() => { onSave(v); setDirty(false); }} disabled={!dirty}
        style={{ marginTop: 8, backgroundColor: dirty ? '#333' : '#CCC', color: '#FFF', border: 'none', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.8rem', cursor: dirty ? 'pointer' : 'default' }}>
        {dirty ? 'Save signature' : 'Saved'}
      </button>
    </div>
  );
}
